/**
 * Main VulForm React component
 */

import React, { useCallback } from 'react';
import { VulFormProps } from '@vulform/core';
import { useVulForm } from '../hooks/useVulForm';
import FormField from './FormField';
import FormLoader from './FormLoader';
import FormSuccess from './FormSuccess';
import FormError from './FormError';

export const VulForm: React.FC<VulFormProps> = ({
  templateId,
  apiKey,
  apiUrl,
  theme = 'auto',
  className = '',
  style,
  prefill = {},
  onSuccess,
  onError,
  onValidationError,
  onFieldChange,
  enableAnalytics = true,
  spamProtection = true,
  loadingState = 'default',
  resetOnSuccess = true,
  showSuccessMessage = true,
  autoRedirect = false,
}) => {
  const {
    template,
    loading,
    error,
    submitting,
    submitted,
    formData,
    validationErrors,
    updateField,
    submitForm,
    resetForm,
  } = useVulForm({
    templateId,
    apiKey,
    apiUrl,
    prefill,
    onSuccess,
    onError,
    onValidationError,
    enableAnalytics,
    spamProtection,
  });

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      updateField(fieldName, value);
      onFieldChange?.(fieldName, value);
    },
    [updateField, onFieldChange]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await submitForm();

      if (resetOnSuccess && !Object.keys(validationErrors).length) {
        resetForm();
      }
    },
    [submitForm, resetOnSuccess, validationErrors, resetForm]
  );

  // Apply theme styles
  const getThemeStyles = (): React.CSSProperties => {
    if (theme === 'auto' && template?.theme) {
      return {
        '--vulform-primary': template.theme.primaryColor,
        '--vulform-secondary': template.theme.secondaryColor,
        '--vulform-background': template.theme.backgroundColor,
        '--vulform-text': template.theme.textColor,
        '--vulform-border': template.theme.borderColor,
        '--vulform-border-radius': template.theme.borderRadius,
        '--vulform-font-family': template.theme.fontFamily,
        '--vulform-font-size': template.theme.fontSize,
        '--vulform-spacing': template.theme.spacing,
        fontFamily: template.theme.fontFamily,
        fontSize: template.theme.fontSize,
        color: template.theme.textColor,
        backgroundColor: template.theme.backgroundColor,
        ...style,
      } as React.CSSProperties;
    }
    return style || {};
  };

  // Get layout classes
  const getLayoutClasses = (): string => {
    if (!template) return 'space-y-6';

    const hasCustomWidths = template.fields.some(field => field.width && field.width !== 'full');

    switch (template.layout) {
      case 'horizontal':
        return 'grid grid-cols-1 md:grid-cols-2 gap-6';
      case 'grid':
        return 'grid grid-cols-12 gap-6';
      default:
        return hasCustomWidths ? 'grid grid-cols-12 gap-6' : 'space-y-6';
    }
  };

  // Get field width classes
  const getFieldWidth = (width?: string): string => {
    switch (width) {
      case 'full':
        return 'col-span-full';
      case 'half':
        return 'col-span-full md:col-span-6';
      case 'third':
        return 'col-span-full md:col-span-4';
      case 'quarter':
        return 'col-span-full md:col-span-3';
      default:
        return 'col-span-full';
    }
  };

  // Loading state
  if (loading) {
    return (
      <FormLoader loadingState={loadingState} className={className} style={getThemeStyles()} />
    );
  }

  // Error state
  if (error) {
    return (
      <FormError
        error={error}
        onRetry={() => window.location.reload()}
        className={className}
        style={getThemeStyles()}
      />
    );
  }

  // Success state
  if (submitted && showSuccessMessage) {
    return (
      <FormSuccess
        message={template?.settings?.successMessage}
        onReset={resetForm}
        className={className}
        style={getThemeStyles()}
      />
    );
  }

  // No template
  if (!template) {
    return (
      <FormError
        error={{ code: 'NO_TEMPLATE', message: 'Form template not found' }}
        className={className}
        style={getThemeStyles()}
      />
    );
  }

  return (
    <div className={`vulform-container ${className}`} style={getThemeStyles()}>
      {/* Custom CSS injection */}
      {template.theme?.customCss && (
        <style dangerouslySetInnerHTML={{ __html: template.theme.customCss }} />
      )}

      <form onSubmit={handleSubmit} className='vulform-form'>
        {/* Form Header */}
        {template.name && (
          <div className='vulform-header mb-6'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-2'>{template.name}</h2>
            {template.description && <p className='text-gray-600'>{template.description}</p>}
          </div>
        )}

        {/* Form Fields */}
        <div className={getLayoutClasses()}>
          {template.fields.map(field => (
            <div key={field.id} className={getFieldWidth(field.width)}>
              <FormField
                field={field}
                value={formData[field.name]}
                error={validationErrors[field.name]}
                onChange={value => handleFieldChange(field.name, value)}
                disabled={submitting}
              />
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className='vulform-submit-section mt-8 pt-6 border-t border-gray-200'>
          <button
            type='submit'
            disabled={submitting}
            className={`
              vulform-submit-button w-full px-6 py-3 text-white font-medium rounded-lg
              transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
              ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }
            `}
            style={{
              backgroundColor: template.theme?.primaryColor || undefined,
            }}
          >
            {submitting ? 'Sending...' : template.settings?.submitText || 'Submit'}
          </button>
        </div>

        {/* General Error Message */}
        {error && (
          <div className='vulform-error-message mt-4 p-4 bg-red-50 border border-red-200 rounded-lg'>
            <p className='text-red-800 text-sm'>{(error as any)?.message || 'An error occurred'}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default VulForm;
